import { getPreviewImage } from "shared-ui/Image/utils";
import { newServingAmount } from "shared-ui/ServingAmount/utils";
import DialogContent from "apps/web/src/components/MenuItem/Dialog/Content";
import { buildRecipeMenuItemInitState } from "apps/web/src/components/MenuItem/Dialog/recipeToMenuItemInitState";
import { MenuItemState } from "apps/web/src/components/MenuItem/reducer";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import LoadingBackdrop from "apps/web/src/components/universal/LoadingBackdrop";
import { useRecipeSaveAsMenuItemDialogQuery } from "apps/web/src/types";
import React, { useEffect } from "react";

interface RecipeToMenuItemDialogProps {
  recipeId: string;
  open: boolean;
  onClose: () => void;
  onSave: (state: MenuItemState) => void;
  saving: boolean;
}

export const RecipeToMenuItemDialog = ({ recipeId, open, onClose, onSave, saving }: RecipeToMenuItemDialogProps) => {
  const { setMessage } = useSnackbar();
  const { data, error, loading } = useRecipeSaveAsMenuItemDialogQuery({
    variables: { id: recipeId },
  });

  const recipe = data?.recipe;
  const invalidPayload = !loading && !error && (!recipe || recipe.servings.length === 0);

  useEffect(() => {
    if (error || invalidPayload) {
      setMessage("error", "Could not load recipe to save as menu item");
      onClose();
    }
  }, [error, invalidPayload, onClose, setMessage]);

  if (loading || saving) {
    return <LoadingBackdrop open={open} onClose={onClose} />;
  }
  if (!recipe || recipe.servings.length === 0) {
    return null;
  }

  const { id, name, images, servings } = recipe;
  const previewImage = getPreviewImage(images);
  const defaultServing = servings.find(s => s.isDefault) || servings[0];
  const recipeServingAmount = newServingAmount({ ...defaultServing, foodOrRecipe: recipe }, 1);

  return (
    <DialogContent
      open={open}
      onClose={onClose}
      onSave={onSave}
      title="Create Reusable Menu Item"
      variant="MenuItem"
      initState={buildRecipeMenuItemInitState({
        id,
        name,
        imageUrl: previewImage?.url ?? null,
        servingAmounts: [recipeServingAmount],
      })}
    />
  );
};
